import is from '@sindresorhus/is';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';
import { z } from 'zod';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { skillTypeLiteralSchema } from '../../../../shared/source/data-contract/filter-response';
import {
    createPreDefinedFilterSchemas,
    createSortFilterStateByOrder,
    customFilterKeySchema,
    customFilterStateSchema,
    getFilterValuesFromFilterValueString,
    reduceFiltersToKeyValuePairs,
    setFilterStateValuesToFilter,
    skillGapRelativeFilterStateSchema,
    skillGapRelativeLiteralSchema,
    skillTypeFilterStateSchema,
    skillTypeTypeLiteralSchema,
} from '../../../../shared/source/data-contract/filter-state';
import { filterResponseTransformedSchema } from '../../data-contract/filter-response-transformed';
import { convertFilterStateToFilters } from '../common/filter-sidebar/utils';

import type { GeneralFilter } from '../../../../shared/source/data-contract/filter';
import type { FilterState, FilterWithFilterStates } from '../../../../shared/source/data-contract/filter-state';
import type { Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { FilterInFilterResponseTransformed } from '../../data-contract/filter-response-transformed';
import type { UseQueryResult } from '@tanstack/react-query';

const allowedTalentFilterInOrder = [skillGapRelativeFilterStateSchema, skillTypeFilterStateSchema] as const;

const filterOrder = allowedTalentFilterInOrder.map(({ shape }) => shape.field.value);
const allowedTalentFilterFieldNameSchema = z.union([skillGapRelativeLiteralSchema, skillTypeTypeLiteralSchema]);

const allowedTalentAndCustomFilterFieldNameSchema = allowedTalentFilterFieldNameSchema.or(customFilterKeySchema);
const allowedTalentFilterSchema = z.union(allowedTalentFilterInOrder);
const allowedTalentAndCustomFilterSchema = allowedTalentFilterSchema.or(customFilterStateSchema);

type AllowedTalentFilter = z.infer<typeof allowedTalentFilterSchema>;

type AllowedTalentOrCustomFilter = z.infer<typeof allowedTalentAndCustomFilterSchema>;

const sortFilters = createSortFilterStateByOrder({ filterOrder });

const isAllowedOrCustomFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedTalentOrCustomFilter =>
    allowedTalentAndCustomFilterFieldNameSchema.safeParse(filter.field).success;

const isAllowedFilter = (filter: FilterInFilterResponseTransformed): filter is AllowedTalentFilter =>
    allowedTalentFilterFieldNameSchema.safeParse(filter.field).success;

const filterQueryParam = 'talentDirectorySkillFilters';

export type UseTalentDirectorySkillFilterResult = {
    readonly filterPayload: Maybe<GeneralFilter>;
    readonly filters: readonly FilterWithFilterStates[];
    readonly hasActiveFilters: boolean;
    readonly resetFilterValues: () => void;
    readonly saveFilterValues: (updatedFilters: readonly FilterWithFilterStates[]) => void;
};

export const useTalentDirectorySkillFilter = (
    skillIdsToBeFilteredFor: readonly string[],
): UseQueryResult<UseTalentDirectorySkillFilterResult> => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language, locationSearchParams } = applicationState;
    const pathname: Pathname = `/global/directory/skills/filter`;

    const resetFilterValues = (): void => {
        applicationState.locationSearchParams.delete(filterQueryParam);
        actions.setLocationSearchParams(applicationState.locationSearchParams);
    };

    const saveFilterValues = (updatedFilters: readonly FilterWithFilterStates[]): void => {
        const filterKeyValuePairs = reduceFiltersToKeyValuePairs(updatedFilters);

        if (is.emptyObject(filterKeyValuePairs)) {
            resetFilterValues();
        } else {
            applicationState.locationSearchParams.set(filterQueryParam, JSON.stringify(filterKeyValuePairs));
            actions.setLocationSearchParams(applicationState.locationSearchParams);
        }
    };

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(filterResponseTransformedSchema, {
                pathname,
            });

            if (result.isOk) {
                const allowedTalentFilterSchemas = createPreDefinedFilterSchemas({ translate });

                const filterStates = result.value
                    .filter(isAllowedOrCustomFilter)
                    .sort(sortFilters)
                    .map((filter) => {
                        if (isAllowedFilter(filter)) {
                            const filterResult = allowedTalentFilterSchemas[filter.field].safeParse(filter);

                            if (filterResult.success) {
                                return filterResult.data;
                            }
                        }

                        return {
                            ...filter,
                            label: translate(`attributes.user.${filter.field.replace('custom.', '')}`),
                        };
                    });

                return [
                    {
                        id: 'skillGap',
                        filterStates: filterStates.filter(({ field }) => field === skillGapRelativeLiteralSchema.value),
                        subTitle: translate('pages.talent-directory.filters.skill-gap.subTitle'),
                        title: translate('pages.talent-directory.filters.skill-gap.title'),
                    },
                    {
                        id: 'skills',
                        filterStates: filterStates.filter(({ field }) => field !== skillGapRelativeLiteralSchema.value),
                        subTitle: translate('pages.talent-directory.filters.skills.subTitle'),
                        title: translate('pages.talent-directory.filters.skills.title'),
                    },
                ];
            }

            throw result.error;
        },
        queryKey: [pathname, 'get', language.code],
        select(filters) {
            const filterValuesJsonString = locationSearchParams.get(filterQueryParam);
            const filterStatesSkills: readonly FilterState[] = [
                {
                    field: 'skill',
                    label: 'skill',
                    type: skillTypeLiteralSchema.value,
                    value: [...skillIdsToBeFilteredFor],
                },
            ];
            if (is.string(filterValuesJsonString)) {
                const filterValuesFromStorage = getFilterValuesFromFilterValueString({
                    filters,
                    filterValuesJsonString,
                });

                if (filterValuesFromStorage.isOk) {
                    const filtersWithValues = setFilterStateValuesToFilter(filters, filterValuesFromStorage.value);
                    const allFilterStatesWithValues = filtersWithValues.reduce(
                        (acc, { filterStates }) => [...acc, ...filterStates],
                        [],
                    );

                    return {
                        filterPayload: Maybe.of(
                            convertFilterStateToFilters([...allFilterStatesWithValues, ...filterStatesSkills]),
                        ),
                        filters: filtersWithValues,
                        hasActiveFilters: allFilterStatesWithValues.some(({ value }) => !is.nullOrUndefined(value)),
                        resetFilterValues,
                        saveFilterValues,
                    };
                }
            }

            return {
                filterPayload: Maybe.of(convertFilterStateToFilters(filterStatesSkills)),
                filters,
                hasActiveFilters: false,
                resetFilterValues,
                saveFilterValues,
            };
        },
    });
};
